<template>
  <!-- Q5 "Others waiting on us" from the Work Progress design: our unfinished tasks that another
       team's task lists as a prerequisite, most-waited-on first -->
  <v-card class="waiting-card">
    <div class="head">
      <div class="titles">
        <h2 class="card-title">{{ $t('taskWaiting.title') }}</h2>
        <span class="sub">{{ $t(teamScoped ? 'taskWaiting.sub' : 'taskWaiting.subAll') }}</span>
      </div>
      <v-chip size="small" variant="tonal" :color="rows.length ? 'info' : undefined">{{ rows.length.toLocaleString() }}</v-chip>
    </div>

    <v-skeleton-loader v-if="loading && !rows.length" type="list-item-two-line@3" />

    <template v-else>
      <!-- Every row, scrolling inside the card like the Recent activity table -->
      <div v-if="rows.length" class="scroll">
        <div
          v-for="row in rows"
          :key="row.taskId"
          class="row row--clickable"
          :class="{ 'is-selected': selected === row.taskId, 'is-dimmed': selected && selected !== row.taskId }"
          role="button"
          tabindex="0"
          :aria-pressed="selected === row.taskId"
          @click="toggleWaitingTaskFilter(row.taskId)"
          @keydown.enter.prevent="toggleWaitingTaskFilter(row.taskId)"
        >
          <div class="row-top">
            <span class="name" :title="row.name">{{ row.name }}</span>
            <v-chip size="x-small" variant="tonal" :color="row.chipColor" class="chip">{{ row.chip }}</v-chip>
          </div>
          <div class="row-bottom">
            <span class="owner">{{ $t('taskWaiting.done', { pct: row.actual }) }}</span>
            <span class="tk-info holds">{{ $t('taskWaiting.waiting', { n: row.waiting }) }}{{ row.teams ? ' · ' + row.teams : '' }}</span>
          </div>
        </div>
      </div>

      <div v-if="!rows.length" class="empty text-medium-emphasis">
        <div>{{ $t('taskWaiting.empty') }}</div>
        <div v-if="!hasPrerequisites" class="note">{{ $t('taskWaiting.noData') }}</div>
      </div>
    </template>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { waitingOnUsSql } from '@/utils/taskTables';
import { taskSCurveFilters, taskScopeKey, taskScope, isTaskScopeReady, toggleWaitingTaskFilter } from '@/utils/taskSCurveFilters';
import { formatDayMonth } from '@/utils/taskDates';


const store = useStore();
const { t } = useI18n();
const conn = computed(() => store.getters.getDBConnection);
const dataLoaded = computed(() => store.getters.getDataLoaded);
const locale = computed(() => store.getters.currentLocale);

const raw = ref([]);
const loading = ref(false);
// Whether the tasks API gave us any prerequisite links at all (drives the empty-state note)
const hasPrerequisites = ref(true);

let queryVersion = 0;

const queryRows = async (query) => {
  const result = await conn.value.query(query);
  return result.toArray().map(row => row.toJSON());
};

const loadData = async () => {
  if (!dataLoaded.value || !isTaskScopeReady.value) return;
  const version = ++queryVersion;
  loading.value = true;
  try {
    const [result, [links]] = await Promise.all([
      // Ignore the waiting cross-filter here: this card is where it is picked
      queryRows(waitingOnUsSql(taskScope(['waiting']))),
      queryRows(`SELECT COUNT(*) AS n FROM task_prerequisites WHERE prerequisiteTaskId IS NOT NULL`),
    ]);
    if (version !== queryVersion) return; // a newer request superseded this one
    raw.value = result;
    hasPrerequisites.value = Number(links?.n ?? 0) > 0;
  } catch (error) {
    if (version !== queryVersion) return;
    console.error('[TaskWaitingOnUsCard] query failed:', error.message);
    raw.value = [];
    hasPrerequisites.value = false;
  } finally {
    if (version === queryVersion) loading.value = false;
  }
};

// Clicking a row cross-filters the other visuals to that task
const selected = computed(() => taskSCurveFilters.waitingTaskIds[0] ?? null);

// Same status rules as the Current tasks card, as a chip with the planned end beside it
const statusChip = (actual, planned) => {
  if (actual > 0 && actual >= (planned ?? 0)) return { label: t('taskStatus.onSchedule'), color: 'success' };
  if (actual > 0) return { label: t('taskStatus.behindSchedule'), color: 'warning' };
  if ((planned ?? 0) > 0) return { label: t('taskStatus.overdue'), color: 'error' };
  return { label: t('taskStatus.upcoming'), color: undefined };
};

const rows = computed(() => raw.value.map((r) => {
  const actual = Number(r.actualPct ?? 0);
  const planned = r.plannedPct === null || r.plannedPct === undefined ? null : Number(r.plannedPct);
  const status = statusChip(actual, planned);
  const due = r.plannedEndDate ? ` · ${t('taskWaiting.due', { date: formatDayMonth(r.plannedEndDate, locale.value) })}` : '';
  return {
    taskId: r.taskId,
    name: r.taskTitle,
    chip: `${status.label}${due}`,
    chipColor: status.color,
    owner: r.ownerTeams ?? '',
    actual: Math.round(actual),
    waiting: Number(r.waitingCount ?? 0),
    teams: r.waitingTeams ?? '',
  };
}));

// "Us" is only a team when one is selected; otherwise the card lists tasks any other task waits on
const teamScoped = computed(() => taskSCurveFilters.teamIds.length > 0 || (taskSCurveFilters.lockedTeamIds ?? []).length > 0);

onMounted(loadData);
watch(
  [dataLoaded, taskScopeKey],
  loadData,
  { deep: true }
);
</script>

<style scoped>
.waiting-card {
  min-width: 0;
  width: 100%;
  margin: 0; /* App.vue gives every v-card margin: 0 auto, which shrinks a grid item to its content */
  height: 100%;
  display: flex;
  flex-direction: column;
}
.head {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px 6px;
}
.titles {
  display: flex;
  flex-direction: column;
}
.card-title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 400;
}
.sub {
  font-size: 12px;
  opacity: 0.55;
}
.row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 20px;
  border-top: 1px solid var(--tk-divider);
}
.row--clickable {
  cursor: pointer;
  transition: opacity 0.2s ease, background-color 0.2s ease;
}
.row--clickable:hover {
  background: var(--tk-row-hover);
}
.row--clickable.is-selected {
  background: var(--tk-row-hover);
  box-shadow: inset 3px 0 0 var(--tk-info);
}
.row--clickable.is-dimmed {
  opacity: 0.45;
}
.row-top,
.row-bottom {
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
}
.row-bottom {
  font-size: 12px;
  opacity: 0.7;
}
.name {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.chip {
  flex: 0 0 auto;
}
.owner {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.holds {
  flex: 0 0 auto;
  opacity: 1;
}
.scroll {
  overflow: auto;
  max-height: 300px;
}
.empty {
  padding: 24px 20px;
  border-top: 1px solid var(--tk-divider);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.note {
  font-size: 12px;
}
</style>
